import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Icon over a caption: Filter, Labels, Reset. Transparent, terracotta text.
 * Source: board 18 (52px tall, 18px icon, 12px caption, 3px gap); board 20 is 56px / 13px.
 * `disabled` dims the button and sets aria-disabled but keeps it focusable, so the
 * control can still be discovered; clicks are ignored.
 *
 * @param {object} props
 * @param {React.ReactNode} props.icon An icon element, e.g. <FilterIcon />.
 * @param {string} props.caption Short caption under the icon.
 * @param {() => void} [props.onClick]
 * @param {boolean} [props.disabled]
 * @param {string} [props.ariaLabel] Defaults to the caption; set it when the caption is
 *   a short state ("Names") and the button needs a fuller name ("Labels: names").
 * @param {'sm'|'md'} [props.size] 'sm' = phone (default); 'md' = desktop (56px, 13px caption).
 * @param {string} [props.className]
 */
export function QuickButton({ icon, caption, onClick, disabled = false, ariaLabel, size = 'sm', className }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel || caption}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-[3px] rounded-[6px] text-root-text transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        size === 'md' ? 'h-14' : 'h-[52px]',
        disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-tile/50',
        className,
      )}
    >
      {icon}
      <span className={cn('font-bold leading-none', size === 'md' ? 'text-[13px]' : 'text-xs')}>{caption}</span>
    </button>
  );
}
